import React from 'react';

import { type EventExtraData, getContrastColorTo, getTranslationText, hexAlpha, isDefined, isEmpty, mapDefined, noop } from '@nfctron/nexus-core';
import {
	UiButton,
	UiDrawer,
	UiDrawerContent,
	UiDrawerFooter,
	UiDrawerHeader,
	UiDrawerTitle,
	UiFeatureIcon,
	cn,
	useContextRequired,
} from '@nfctron/nexus-react';

import { Divider, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { openConfirmModal } from '@mantine/modals';
import { ClipboardCopyIcon, RotateCcwIcon, StarHalfIcon, XIcon } from 'lucide-react';

import { getEventTypeSensitiveComponentDisplayName } from '@root/pages/event/[organizer]/[event]/_resources/helpers/common';

import { TextInput } from '@root/features/form/components/TextInput/TextInput';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { useTracking } from '@root/lib/modules/tracking/TrackingProvider';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../../components/CartContext';
import { cartStoreSelectors, useCartStore, useCartStoreCtx, usePromoCodesApi, useTicketTypesApiQuery } from '../../stores/cartStore';

import { TicketsViewContentAtom } from './TicketsViewContent.atom';

const PromoAndBenefitCodes: React.FC<{ promoSheetOpen: boolean; promoSheetController: ReturnType<typeof useDisclosure>[1] }> = (props) => {
	const { promoSheetOpen, promoSheetController } = props;
	const { event } = useContextRequired(EventCartPageContext);
	const { t, lang } = useTranslate(APP_I18N_NAMESPACE.MAIN, TicketsViewContentAtom.displayName);

	const { trackingModule } = useTracking();
	const ticketTypesListed = useCartStore(cartStoreSelectors.listTicketTypes);
	const loadTicketTypesConfig = useCartStore((state) => state.loadTicketTypesConfig);
	const removeTicketsFromCartByTicketType = useCartStore((state) => state.removeAllCartedTicketsByTicketType);
	const { benefitCodeApi, promoCodeApi } = usePromoCodesApi();

	const [promoValue, setPromoValue] = React.useState<string>('');

	const submitPromoCode = async (promoCode: string) => {
		try {
			/** get promo code result from API */
			const promoRes = await promoCodeApi.mutateAsync({
				codes: [promoCode],
				type: 'promo',
			});
			/** remove all possibly already carted tickets that will be removed now */
			removeTicketsFromCartByTicketType({
				ticketTypeIds: promoRes.originalTicketTypeIdsToHide,
				trackingModule,
			});

			/** load ticket types config */
			loadTicketTypesConfig({
				ticketTypes: [
					/** hide original ticket types from already existing list of ticket types config */
					...ticketTypesListed.map((tt) => {
						const isToHide = promoRes.originalTicketTypeIdsToHide.includes(tt.ticketTypeId);
						if (isToHide) return { ...tt, __isUnlistable: true };
						return tt;
					}),
					/** add new ticket types */
					...promoRes.newTicketTypes,
				],
				forceOverwrite: false,
				duplicatesStrategy: 'replace',
				shouldAutoCart: false,
			});
		} catch (e) {
			/* ignore error (toast will show) */
		} finally {
			/** close promo sheet */
			promoSheetController.close();
			/** clear promo code input */
			setPromoValue('');
		}
	};

	/** benefit code providers */
	const [openedBenefitCodeProvider, setOpenedBenefitCodeProvider] = React.useState<EventExtraData['benefitCodeProviders'][number] | null>(null);
	const [isBenefitCodeSheetOpen, benefitCodeSheetController] = useDisclosure(false);
	const [providerBenefitCodeValue, setProviderBenefitCodeValue] = React.useState<string>('');
	const submitBenefitCode = async (rawCode: string, prefix: string) => {
		const benefitCode = `${prefix}${rawCode}`;
		try {
			/** get benefit code result from API */
			const benefitRes = await benefitCodeApi.mutateAsync({
				codes: [benefitCode],
			});
			/** remove all possibly already carted tickets that will be removed now */
			removeTicketsFromCartByTicketType({
				ticketTypeIds: benefitRes.originalTicketTypeIdsToHide,
				trackingModule,
			});
			/** update available ticket types */
			loadTicketTypesConfig({
				ticketTypes: [
					/** hide original ticket types from already existing list of ticket types config */
					...ticketTypesListed.map((tt) => {
						const isToHide = benefitRes.originalTicketTypeIdsToHide.includes(tt.ticketTypeId);
						if (isToHide) return { ...tt, __isUnlistable: true };
						return tt;
					}),
					/** add new ticket types */
					...benefitRes.newTicketTypes,
				],
				forceOverwrite: false,
				duplicatesStrategy: 'replace',
				shouldAutoCart: false,
			});
		} catch (e) {
			/* ignore error (toast will show) */
		} finally {
			/** close benefit sheet */
			benefitCodeSheetController.close();
			setTimeout(() => setOpenedBenefitCodeProvider(null), 300);
		}
	};
	return (
		<React.Fragment>
			{/* promo code sheet */}
			<UiDrawer open={promoSheetOpen} onClose={promoSheetController.close} repositionInputs={false}>
				<UiDrawerContent className="mx-auto max-w-screen-sm">
					<UiDrawerHeader className="flex justify-center border-b border-zinc-200 py-6">
						<UiDrawerTitle asChild>
							<Text fw={700} c="lucky-point" fz={18}>
								{t('promoCodeSheet.title')}
							</Text>
						</UiDrawerTitle>
					</UiDrawerHeader>

					<form
						className="flex max-h-full flex-col gap-6 overflow-scroll p-4"
						onSubmit={async (e) => {
							/** prevent form submission */
							e.preventDefault();
							e.stopPropagation();
							/** submit promo code callback */
							await submitPromoCode(promoValue);
						}}
					>
						{/* icon */}
						<UiFeatureIcon size="default" color="zinc">
							<StarHalfIcon />
						</UiFeatureIcon>
						{/* input */}
						<TextInput
							label={t('promoCodeSheet.inputLabel')}
							description={t('promoCodeSheet.text')}
							placeholder={t('promoCodeSheet.inputPlaceholder')}
							value={promoValue}
							onChange={(e) => setPromoValue(e.target.value)}
							{...(typeof window !== 'undefined' &&
								'clipboard' in window.navigator && {
									clas: {
										rightSection: 'max-w-unset',
									},
									rightSection: (
										<UiButton
											size="unstyled"
											variant="ghost"
											leftSection={<ClipboardCopyIcon className="size-4" />}
											onClick={(e) => {
												window.navigator.clipboard
													.readText()
													.then((text) => {
														setPromoValue(text);
														/** submit promo code callback */
														submitPromoCode(text);
													})
													.catch(noop);
											}}
										/>
									),
								})}
						/>

						{/* benefit code providers */}
						{event.extraData.benefitCodeProviders.length > 0 && (
							<div className="flex flex-col gap-3">
								{/* divider */}
								<Divider labelPosition="center" classNames={{ label: 'text-zinc-500' }} label={t('promoCodeSheet.dividerLabel')} />

								{/* benefit code buttons */}
								{event.extraData.benefitCodeProviders.map((benefitCodeProvider) => {
									return (
										<UiButton
											key={benefitCodeProvider.providerId}
											size="lg"
											type="button"
											variant="primary"
											fullWidth
											{...(!isEmpty(benefitCodeProvider.color) && {
												variant: 'unstyled',
												color: 'unstyled',
												style: {
													backgroundColor: hexAlpha(benefitCodeProvider.color),
													color: getContrastColorTo(hexAlpha(benefitCodeProvider.color)),
												},
											})}
											loading={benefitCodeApi.isPending}
											onClick={() => {
												promoSheetController.close();
												setOpenedBenefitCodeProvider(benefitCodeProvider);
												benefitCodeSheetController.open();
											}}
										>
											{!isEmpty(benefitCodeProvider.buttonLogoUrl) && (
												<img src={benefitCodeProvider.buttonLogoUrl} className="size-6 object-scale-down" alt="Logo" />
											)}
											<span>{getTranslationText(benefitCodeProvider.buttonText, lang) ?? benefitCodeProvider.name}</span>
										</UiButton>
									);
								})}
							</div>
						)}
					</form>

					<UiDrawerFooter className="flex items-center gap-3">
						<UiButton
							size="lg"
							type="submit"
							variant="primary"
							color="lucky-point"
							className="h-12"
							fullWidth
							loading={promoCodeApi.isPending}
							onClick={() => {
								/** submit promo code callback */
								submitPromoCode(promoValue);
							}}
						>
							{t('promoCodeSheet.submitButton')}
						</UiButton>
					</UiDrawerFooter>
				</UiDrawerContent>
			</UiDrawer>

			{/* benefit code provider sheet */}
			<UiDrawer
				open={isBenefitCodeSheetOpen}
				onClose={() => {
					benefitCodeSheetController.close();
					setProviderBenefitCodeValue('');
					setTimeout(() => setOpenedBenefitCodeProvider(null), 300);
				}}
				repositionInputs={false}
			>
				<UiDrawerContent className="mx-auto max-w-screen-sm">
					<UiDrawerHeader className="flex justify-center border-b border-zinc-200 py-6">
						<UiDrawerTitle asChild>
							<Text fw={700} c="lucky-point" fz={18}>
								{getTranslationText(openedBenefitCodeProvider?.sheetTitle, lang) ?? t('benefitCodeProviderSheet.title')}
							</Text>
						</UiDrawerTitle>
					</UiDrawerHeader>

					<form
						className="flex flex-col gap-6 p-4"
						onSubmit={async (e) => {
							/** prevent form submission */
							e.preventDefault();
							e.stopPropagation();
							/** submit benefit code callback */
							await submitBenefitCode(providerBenefitCodeValue, openedBenefitCodeProvider?.prefix ?? '');
						}}
					>
						{/* icon */}
						{!isEmpty(openedBenefitCodeProvider?.logoUrl) && (
							<UiFeatureIcon size="unstyled" color="zinc" className="size-24 p-3">
								<img src={openedBenefitCodeProvider?.logoUrl} className="size-10 object-scale-down" alt="Logo" />
							</UiFeatureIcon>
						)}
						{/* input */}
						<TextInput
							label={getTranslationText(openedBenefitCodeProvider?.sheetInputLabel, lang) ?? t('benefitCodeProviderSheet.inputLabel')}
							description={getTranslationText(openedBenefitCodeProvider?.sheetInputDescription, lang) ?? t('benefitCodeProviderSheet.text')}
							placeholder={
								getTranslationText(openedBenefitCodeProvider?.sheetInputPlaceholder, lang) ?? t('benefitCodeProviderSheet.inputPlaceholder')
							}
							value={providerBenefitCodeValue}
							onChange={(e) => setProviderBenefitCodeValue(e.target.value)}
							{...(typeof window !== 'undefined' &&
								'clipboard' in window.navigator && {
									rightSection: (
										<UiButton
											size="unstyled"
											variant="ghost"
											leftSection={<ClipboardCopyIcon className="size-4" />}
											onClick={(e) => {
												window.navigator.clipboard
													.readText()
													.then(async (text) => {
														setProviderBenefitCodeValue(text);
														/** submit promo code callback */
														await submitBenefitCode(text, openedBenefitCodeProvider?.prefix ?? '');
													})
													.catch(noop);
											}}
										/>
									),
								})}
						/>
					</form>

					<UiDrawerFooter className="flex items-center gap-3">
						<UiButton
							size="lg"
							type="submit"
							fullWidth
							{...(isDefined(openedBenefitCodeProvider) &&
								!isEmpty(openedBenefitCodeProvider.color) && {
									variant: 'unstyled',
									color: 'unstyled',
									style: {
										backgroundColor: hexAlpha(openedBenefitCodeProvider.color),
										color: getContrastColorTo(hexAlpha(openedBenefitCodeProvider.color)),
									},
								})}
							loading={benefitCodeApi.isPending}
							onClick={async () => {
								/** submit promo code callback */
								await submitBenefitCode(providerBenefitCodeValue, openedBenefitCodeProvider?.prefix ?? '');
							}}
						>
							{getTranslationText(openedBenefitCodeProvider?.sheetSubmitButtonLabel, lang) ?? t('benefitCodeProviderSheet.submitButton')}
						</UiButton>
					</UiDrawerFooter>
				</UiDrawerContent>
			</UiDrawer>
		</React.Fragment>
	);
};

/**
 * Event/Cart/TicketsViewHeader atom component
 * @author filipditrich <ditrich@nfctron.com>
 */
export const TicketsViewHeaderAtom: AtomComponent = () => {
	const { event } = useContextRequired(EventCartPageContext);
	const cartStore = useCartStoreCtx();
	const cartConfig = cartStore.useCartConfig();
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, TicketsViewContentAtom.displayName);
	const { t: typeSensitiveT } = useTranslate(
		APP_I18N_NAMESPACE.MAIN,
		getEventTypeSensitiveComponentDisplayName(TicketsViewContentAtom.displayName, cartConfig.eventTypeId),
	);
	const { trackingModule } = useTracking();
	const ticketTypesConfig = cartStore.useTicketTypesConfig();
	const removeTicketTypesFromConfig = useCartStore((state) => state.removeTicketTypesFromConfig);

	/** promo codes */
	const [promoSheetOpen, promoSheetController] = useDisclosure(false);
	const { promoCodeApi } = usePromoCodesApi();
	const orgPartnersApplied = useCartStore((state) => cartStoreSelectors.listOrgPartners(state));
	const promoCodeLimited = React.useMemo(
		() => orgPartnersApplied.length >= (event.extraData.orgPartner?.allowMultipleLimit ?? 1),
		[event.extraData.orgPartner?.allowMultipleLimit, orgPartnersApplied.length],
	);

	const ticketTypesApiQuery = useTicketTypesApiQuery();

	return (
		<React.Fragment>
			<section className="flex flex-col gap-2">
				<header className="my-0.5 flex items-center justify-between gap-2">
					{/* heading */}
					<div className="group group flex items-center gap-2">
						<span className="text-lg font-bold text-zinc-700">{typeSensitiveT('heading')}</span>
						<UiButton
							variant="ghost"
							size="xs"
							className={cn('opacity-0 transition-opacity group-hover:opacity-100', ticketTypesApiQuery.isLoading && 'opacity-100')}
							loading={ticketTypesApiQuery.isLoading}
							leftSection={<RotateCcwIcon />}
							onClick={() => {
								ticketTypesApiQuery.refetch();
							}}
						/>
					</div>

					{/* promo code button */}
					{event.extraData.orgPartner?.isInputShown && (
						<UiButton
							variant="unstyled"
							leftSection={<StarHalfIcon className="size-4" />}
							size="unstyled"
							className="h-7 rounded-full px-4 text-sm data-[disabled=true]:pointer-events-auto! data-[disabled=true]:opacity-25 data-[disabled=true]:grayscale"
							loading={promoCodeApi.isPending}
							onClick={() => promoSheetController.open()}
							style={{
								backgroundColor: mapDefined(event.eventColors.background, hexAlpha),
								color: mapDefined(event.eventColors.subtitle, hexAlpha),
							}}
							/** handle multi promo code limitation */
							{...(promoCodeLimited && {
								disabled: true,
								tooltip: t('promoCodeLimited.tooltip'),
							})}
						>
							{t('promoCodeButton.text')}
						</UiButton>
					)}
				</header>

				{/* applied promo code */}
				{orgPartnersApplied.map((orgPartner) => {
					return (
						<div key={orgPartner.shortCode} className="bg-lucky-point-500 flex items-center gap-1 rounded-md p-2 text-sm font-medium text-white">
							{/* org partner logo */}
							<StarHalfIcon className="size-4" />

							{/* text */}
							<span className="mr-auto">{typeSensitiveT('appliedPromoCode.text', { orgPartnerName: orgPartner.namePub })}</span>
							{/* remove/clear button */}
							<UiButton
								variant="unstyled"
								className="hover:bg-white/10"
								size="xs"
								leftSection={<XIcon />}
								loading={promoCodeApi.isPending}
								onClick={async () => {
									openConfirmModal({
										title: t('promoCodeRemoveConfirm.title'),
										centered: true,
										children: <Text size="md">{t('promoCodeRemoveConfirm.text', { orgPartnerName: orgPartner.namePub })}</Text>,
										labels: {
											confirm: t('promoCodeRemoveConfirm.confirm'),
											cancel: t('promoCodeRemoveConfirm.cancel'),
										},
										confirmProps: { color: 'orange' },
										cancelProps: { color: 'zinc', variant: 'light' },
										onConfirm: () => {
											promoCodeApi.reset();
											/** get all ticket types that will be removed */
											const ticketTypesToRemove = ticketTypesConfig
												.filter((tt) => tt.orgPartner?.shortCode === orgPartner.shortCode)
												.map((tt) => tt.ticketTypeId);
											/** remove all possibly already carted tickets that will be removed now */
											removeTicketTypesFromConfig({
												ticketTypeIds: ticketTypesToRemove,
												trackingModule,
											});
										},
									});
								}}
							/>
						</div>
					);
				})}
			</section>

			{/* promo and benefit codes */}
			<PromoAndBenefitCodes promoSheetOpen={promoSheetOpen} promoSheetController={promoSheetController} />
		</React.Fragment>
	);
};

TicketsViewHeaderAtom.displayName = 'Event/Cart/TicketsViewHeaderAtom';
